import { css } from '@emotion/react'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import { format } from 'date-fns'
import { useState } from 'react'
import { isMobileOnly } from 'react-device-detect'

import { tJosa } from '~/@app/i18n'
import { DROPDOWN_ITEM_HEIGHT } from '~/@common/constants'
import { useOrientation } from '~/@common/hooks/useOrientation'
import { colors, typo } from '~/@common/styles'
import type { CalendarDateRange } from '~/@common/ui/Calendar/Calendar'
import { Dropdown, DropdownItem } from '~/@common/ui/Dropdown'
import { Icon } from '~/@common/ui/Icon/Icon'

import { CasListMobileOptionDrawer } from './CasListMobileOptionDrawer'

type Homework = CasAssignmentDtoT.AssignedCasWithHomework
type StatusTab = 'ALL' | Homework['status']

const STATUS_OPTIONS: { value: StatusTab; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'NOT_STARTED', label: '응시가능' },
  { value: 'IN_PROGRESS', label: '응시중' },
  { value: 'COMPLETED', label: '응시완료' },
]

const STATUS_LABEL: Record<Homework['status'], string> = {
  NOT_STARTED: '응시가능',
  IN_PROGRESS: '응시중',
  COMPLETED: '응시완료',
}

type Props = {
  rows: Homework[]
  statusTab: StatusTab
  dateRange: CalendarDateRange
  onlyHomework: boolean
  dateRangeLabel: string
  onChangeStatusTab: (next: StatusTab) => void
  onChangeFilters: (next: { dateRange: CalendarDateRange; onlyHomework: boolean }) => void
  onRowClick: (row: Homework) => void
}

export const CasListMobileView = ({
  rows,
  statusTab,
  dateRange,
  onlyHomework,
  dateRangeLabel,
  onChangeStatusTab,
  onChangeFilters,
  onRowClick,
}: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { type: orientationType } = useOrientation()
  const isMobileLandscape = isMobileOnly && orientationType === 'landscape'

  return (
    <>
      <CasListMobileOptionDrawer
        isOpened={drawerOpen}
        dateRange={dateRange}
        onlyHomework={onlyHomework}
        dateRangeLabel={dateRangeLabel}
        onChangeOpen={setDrawerOpen}
        onApply={onChangeFilters}
      />

      <section css={S.wrapper}>
        <div css={S.filters}>
          <Dropdown
            width={120}
            listHeight={isMobileLandscape ? DROPDOWN_ITEM_HEIGHT * 2 : undefined}
            value={statusTab}
            onChange={(v) => onChangeStatusTab(v as StatusTab)}
          >
            {STATUS_OPTIONS.map((option) => (
              <DropdownItem key={option.value} value={option.value}>
                {option.label}
              </DropdownItem>
            ))}
          </Dropdown>
          <button className="filter-button" onClick={() => setDrawerOpen(true)}>
            <Icon name="icon_filter" color={'var(--Colors-Blue-600)'} size={16} />
          </button>
        </div>

        {rows.length === 0 ? (
          <div css={S.empty}>
            {tJosa('출제')}된 {tJosa('반응형평가', '이/가')} 없어요.
          </div>
        ) : (
          <ul css={S.list}>
            {rows.map((row) => (
              <li key={row.assignedId}>
                <button css={S.card} onClick={() => onRowClick(row)}>
                  <div css={S.cardTop}>
                    <span className="type">
                      <Icon name="icon_description_paper" size={12} color={'var(--Colors-Blue-600)'} />
                      <p>{tJosa('반응형평가')}</p>
                    </span>
                    <span className="datetime">
                      {format(new Date(row.assignDatetime), 'yy.MM.dd')}
                    </span>
                    <span className="tags">
                      {row.homeworks.length > 0 && <span className="tag">숙제</span>}
                      {row.autoScored && <span className="tag">자동채점</span>}
                      {row.accessModifierToStudent === 'PRIVATE' && (
                        <span className="tag">비공개</span>
                      )}
                      <span className={`tag status ${row.status}`}>{STATUS_LABEL[row.status]}</span>
                    </span>
                  </div>
                  <p css={S.title}>{row.title}</p>
                  <div css={S.cardBottom}>
                    <span className="count">{row.maxProblemCount}문제</span>
                    {row.status === 'COMPLETED' && (
                      <>
                        <span className="divider" />
                        <span className="point">점수 {row.score ?? 0}점</span>
                        <span className="answer">
                          <Icon name="icon_answer_wrong" size={12} color={colors.red.$300} />
                          <span>: {row.wrongCount}개</span>
                        </span>
                        <span className="answer">
                          <Icon name="icon_answer_correct" size={12} color={'var(--Colors-Sky-400)'} />
                          <span>: {row.correctCount}개</span>
                        </span>
                      </>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

const S = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
    padding: 20px 16px 40px;
  `,
  filters: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    .filter-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      padding: 10px;
      border-radius: var(--Radius-300);
      background: var(--White);
      box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.05);
    }
  `,
  empty: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--Colors-Gray-500);
    ${typo.body02};
  `,
  list: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    flex: 1 1 0;
  `,
  card: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 20px;
    background: var(--White);
    border-radius: var(--Radius-300);
    text-align: left;
    cursor: pointer;
  `,
  cardTop: css`
    display: flex;
    align-items: center;
    gap: 6px;
    .type {
      display: flex;
      align-items: center;
      gap: 4px;
      ${typo.caption01};
      font-weight: 700;
      color: var(--Colors-Blue-600);
    }
    .datetime {
      ${typo.caption01};
      color: var(--Colors-Gray-700);
    }
    .tags {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: auto;
    }
    .tag {
      padding: 4px;
      border-radius: var(--Radius-100);
      background-color: var(--Colors-Gray-200);
      color: var(--Colors-Gray-800);
      ${typo.caption02};
      line-height: normal;
    }
    .status.NOT_STARTED {
      background-color: var(--Colors-Blue-600);
      color: var(--White);
    }
    .status.IN_PROGRESS {
      background-color: var(--Colors-Gray-700);
      color: var(--White);
    }
  `,
  title: css`
    ${typo.body02};
    color: var(--Colors-Gray-900);
  `,
  cardBottom: css`
    display: flex;
    align-items: center;
    gap: 6px;
    ${typo.caption01};
    color: var(--Colors-Gray-700);
    .divider {
      width: 1px;
      height: 10px;
      background-color: var(--Colors-Gray-500);
    }
    .point {
      color: var(--Colors-Blue-600);
      font-weight: 700;
    }
    .answer {
      display: inline-flex;
      align-items: center;
      gap: 2px;
    }
  `,
}
